import chatService from '../../services/chat'

const WELCOME = `¡Hola! Soy el **asistente virtual de Avanza Inmuebles**. Puedo ayudarte a:

- 🏠 Buscar vivienda
- 🏢 Buscar local o nave
- 💼 Vender tu inmueble
- 🔑 Alquilar tu inmueble
- 📅 Solicitar una visita
- 🧑‍💼 Hablar con un asesor

Cuéntame qué necesitas y empezamos.`

export const STORAGE_KEY = 'avanza_chat_state'

let nextId = 0
const messageId = () => `m${nextId++}`

function welcomeMessages () {
  return [{ id: messageId(), role: 'assistant', content: WELCOME, properties: [] }]
}

// Rehidrata el estado del chat guardado en sessionStorage (persiste durante la
// visita: sobrevive a navegación y recargas, se reinicia en una visita nueva).
function loadPersisted () {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!Array.isArray(data.messages) || !data.messages.length) return null
    // Continuar la numeración de ids para evitar colisiones de :key
    const maxId = data.messages.reduce((max, m) => {
      const n = parseInt(String(m.id).replace('m', ''), 10)
      return Number.isFinite(n) && n > max ? n : max
    }, -1)
    nextId = maxId + 1
    return data
  } catch (e) {
    return null
  }
}

function initialState () {
  const persisted = loadPersisted()
  if (persisted) {
    return {
      messages: persisted.messages,
      lastProperties: persisted.lastProperties || [],
      isLoading: false,
      sessionId: persisted.sessionId || chatService.getSessionId(),
      isPanelOpen: !!persisted.isPanelOpen
    }
  }
  return {
    messages: welcomeMessages(),
    lastProperties: [],
    isLoading: false,
    sessionId: chatService.getSessionId(),
    isPanelOpen: false
  }
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    hasConversation: (state) => state.messages.some((m) => m.role === 'user')
  },

  mutations: {
    addMessage (state, message) {
      state.messages.push({ id: messageId(), properties: [], ...message })
    },
    setLoading (state, value) {
      state.isLoading = value
    },
    setSessionId (state, sessionId) {
      state.sessionId = sessionId
    },
    setLastProperties (state, properties) {
      state.lastProperties = properties
    },
    setPanelOpen (state, value) {
      state.isPanelOpen = value
    },
    reset (state) {
      state.messages = welcomeMessages()
      state.lastProperties = []
      state.sessionId = null
      state.isLoading = false
    }
  },

  actions: {
    async send ({ commit, state }, text) {
      const message = (text || '').trim()
      if (!message || state.isLoading) return

      commit('addMessage', { role: 'user', content: message })
      commit('setLoading', true)

      try {
        const result = await chatService.sendMessage(message, state.sessionId)
        commit('setSessionId', result.sessionId)
        commit('addMessage', {
          role: 'assistant',
          content: result.reply,
          properties: result.properties
        })
        if (result.properties.length) commit('setLastProperties', result.properties)
      } catch (error) {
        commit('addMessage', { role: 'error', content: error.message })
      } finally {
        commit('setLoading', false)
      }
    },

    openPanel ({ commit }) {
      commit('setPanelOpen', true)
    },

    closePanel ({ commit }) {
      commit('setPanelOpen', false)
    },

    togglePanel ({ commit, state }) {
      commit('setPanelOpen', !state.isPanelOpen)
    },

    // Mensajes predefinidos que la interfaz inyecta en la conversación.
    askAboutProperty ({ dispatch }, ref) {
      dispatch('openPanel')
      return dispatch('send', `Muéstrame más detalles de la referencia ${ref}`)
    },

    scheduleVisit ({ dispatch }, ref) {
      dispatch('openPanel')
      const text = ref
        ? `Quiero agendar una visita para la referencia ${ref}`
        : 'Quiero agendar una visita con un comercial'
      return dispatch('send', text)
    },

    startCatchment ({ dispatch }) {
      dispatch('openPanel')
      return dispatch('send', 'Quiero vender o alquilar mi inmueble con Avanza')
    },

    newConversation ({ commit }) {
      chatService.clearSession()
      try {
        sessionStorage.removeItem(STORAGE_KEY)
      } catch (e) {
        // ignorar
      }
      commit('reset')
    }
  }
}
